import React, { FC, ReactElement } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MessageDescriptor } from 'react-intl';
import { useTheme } from 'styled-components';

import { CURRENCIES } from '@savgroup-front-common/constants';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { formatAmountWithCurrency } from '@savgroup-front-common/core/formatters/intl';
import { MyAccountIcon } from '@savgroup-front-common/core/protons/IconsNewDesign/MyAccount.icon';
import { MessageType } from '@savgroup-front-common/types';
import { ICONS_TYPE } from '@savgroup-front-common/types/Icon';

import { RadioCardWithCheckboxColumn } from '../RadioCardWithCheckboxColumn';

import {
  $Item,
  $ItemContentTextWrapper,
  $ItemContentWrapper,
  $ItemDescription,
  $ItemDistance,
  $ItemHighlighting,
  $ItemHighlightingIcon,
  $ItemLogo,
  $ItemPrice,
} from './ChooseItemCard.styles';
import { CHOOSE_ITEM_CARD_TYPE } from './ChooseItemCard.types';
import ChooseItemCardAddress from './ChooseItemCardAddress';
import messages from './messages';

interface ChooseItemCardMobileViewProps {
  name: string;
  value: string;
  handleOnChange?: (...value: any) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  title?: string | number | boolean | MessageType | MessageDescriptor;
  highlighting?: string | number | boolean | MessageType | MessageDescriptor;
  description?: string | number | boolean | MessageType | MessageDescriptor;
  price?: number;
  icon?: ICONS_TYPE;
  logo?: ReactElement;
  distance?: string | number | boolean | MessageType | MessageDescriptor;
  showAddress?: boolean;
  actionButton?: ReactElement;
  hasDisplayFreeAmount?: boolean;
  chooseItemCardType: CHOOSE_ITEM_CARD_TYPE;
  dataTestId?: string;
}

const ChooseItemCardMobileView: FC<ChooseItemCardMobileViewProps> = ({
  name,
  value,
  handleOnChange = () => undefined,
  onClick = () => undefined,
  title,
  highlighting,
  description,
  distance,
  price,
  icon = undefined,
  logo,
  showAddress = false,
  actionButton,
  hasDisplayFreeAmount = false,
  chooseItemCardType,
  dataTestId,
}) => {
  const theme = useTheme();
  const formContext = useFormContext();
  const { control } = formContext;
  const address = formContext.getValues('addressSelected');

  const hasContentBody =
    highlighting || description || distance || price || logo;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <$Item>
          <RadioCardWithCheckboxColumn
            dataTestId={dataTestId}
            name={field.name}
            image={
              icon ? (
                <MyAccountIcon
                  icon={icon}
                  color={
                    field.value === value
                      ? theme.colors.primary
                      : theme.colors.paragraphTextColor
                  }
                />
              ) : undefined
            }
            imageHovered={
              icon && <MyAccountIcon icon={icon} color={theme.colors.primary} />
            }
            title={title}
            value={value}
            onChange={(e) => {
              if (!showAddress) {
                handleOnChange(e.target.value);
              }

              return field.onChange(e.target.value);
            }}
            isChecked={field.value === value}
            onClick={onClick}
            subLine={
              showAddress ? (
                <ChooseItemCardAddress
                  address={address}
                  value={value}
                  handleOnChange={handleOnChange}
                />
              ) : undefined
            }
            isHidden
          >
            {hasContentBody && (
              <$ItemContentWrapper>
                <$ItemContentTextWrapper>
                  {highlighting && (
                    <$ItemHighlighting>
                      <$ItemHighlightingIcon>
                        <MyAccountIcon
                          icon={ICONS_TYPE.BASELINE_ECO}
                          color={theme.colors.success}
                        />
                      </$ItemHighlightingIcon>
                      <SafeFormattedMessageWithoutSpread
                        message={highlighting}
                      />
                    </$ItemHighlighting>
                  )}
                  {description && (
                    <$ItemDescription>
                      <SafeFormattedMessageWithoutSpread
                        message={description}
                      />
                    </$ItemDescription>
                  )}
                  {distance && (
                    <$ItemDistance>
                      <SafeFormattedMessageWithoutSpread message={distance} />
                    </$ItemDistance>
                  )}
                  {chooseItemCardType ===
                    CHOOSE_ITEM_CARD_TYPE.PICKUP_POINT_CARD &&
                    !!price && (
                      <$ItemPrice
                        $hasPrice
                        $hasPickupPointType
                        data-testid="ChooseItemCardPrice"
                      >
                        {formatAmountWithCurrency(
                          price,
                          CURRENCIES.EUR,
                        )?.toString()}
                      </$ItemPrice>
                    )}
                  {actionButton}
                </$ItemContentTextWrapper>
                {chooseItemCardType === CHOOSE_ITEM_CARD_TYPE.HANDLING_CARD &&
                  !!price && (
                    <$ItemPrice
                      $hasPrice
                      $hasPickupPointType={false}
                      data-testid="ChooseItemCardPrice"
                    >
                      <SafeFormattedMessageWithoutSpread
                        message={messages.startingFrom}
                        values={{
                          price: formatAmountWithCurrency(
                            price,
                            CURRENCIES.EUR,
                          )?.toString(),
                        }}
                      />
                    </$ItemPrice>
                  )}
                {chooseItemCardType === CHOOSE_ITEM_CARD_TYPE.NORMAL_CARD &&
                  !!price && (
                    <$ItemPrice
                      $hasPrice
                      $hasPickupPointType={false}
                      data-testid="ChooseItemCardPrice"
                    >
                      {formatAmountWithCurrency(
                        price,
                        CURRENCIES.EUR,
                      )?.toString()}
                    </$ItemPrice>
                  )}
                {!price && hasDisplayFreeAmount && (
                  <$ItemPrice
                    $hasPrice={false}
                    $hasPickupPointType={false}
                    data-testid="ChooseItemCardPrice"
                  >
                    <SafeFormattedMessageWithoutSpread
                      message={messages.free}
                    />
                  </$ItemPrice>
                )}
                {logo && <$ItemLogo>{logo}</$ItemLogo>}
              </$ItemContentWrapper>
            )}
          </RadioCardWithCheckboxColumn>
        </$Item>
      )}
    />
  );
};

ChooseItemCardMobileView.displayName = 'ChooseItemCardMobileView';

export default ChooseItemCardMobileView;
